import * as Sentry from '@sentry/react';

const API_BASE = import.meta.env.VITE_API_URL || '';

let getAccessToken: (() => Promise<string>) | null = null;

export function setTokenGetter(getter: () => Promise<string>) {
  getAccessToken = getter;
}

export async function getCurrentToken(): Promise<string | null> {
  return getAccessToken ? getAccessToken() : null;
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = getAccessToken ? await getAccessToken() : null;

  const res = await fetch(`${API_BASE}/api${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  });

  if (!res.ok) {
    const error = await res.json().catch(() => ({ error: 'Unknown error' }));
    const err = new Error(error.error || `HTTP ${res.status}`) as Error & { status: number; code?: string };
    err.status = res.status;
    if (error.code) err.code = error.code;
    if (res.status >= 500) {
      Sentry.captureException(err, {
        extra: { status: res.status, method: options.method ?? 'GET', path },
      });
    }
    throw err;
  }

  return res.json();
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'POST', body: JSON.stringify(body) }),
  put: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'PUT', body: JSON.stringify(body) }),
  patch: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};
